import { PlanTier } from '../@types/venue';

// ----------------------------------------------------------------------

/**
 * Single source of truth for what each plan tier unlocks. Anything that
 * needs to check "does this account's plan cover feature X" should go
 * through this file instead of hardcoding its own tier list.
 *
 * Ranking and venue limits mirror mvp-check-landing/constants/pricing.ts.
 * Backend SQL helpers in migration 144 mirror MIN_TIER below.
 */
const TIER_RANK: Record<PlanTier, number> = {
  starter: 0,
  essential: 1,
  pro: 2,
  enterprise: 3,
  custom: 4,
};

export const TIER_LABELS: Record<PlanTier, string> = {
  starter: 'Starter',
  essential: 'Essential',
  pro: 'Pro',
  enterprise: 'Enterprise',
  custom: 'Customizado',
};

/** Max venues allowed per tier — `null` means unlimited. */
export const TIER_VENUE_LIMITS: Record<PlanTier, number | null> = {
  starter: 1,
  essential: 1,
  pro: 3,
  enterprise: 10,
  custom: null,
};

/**
 * Minimum plan tier required for each gated feature.
 * Keep in sync with backend `organization_meets_minimum_tier*`.
 */
export const MIN_TIER = {
  timedMenus: 'essential' as PlanTier,
  tableQr: 'essential' as PlanTier,
  analytics: 'essential' as PlanTier,
  postpaid: 'pro' as PlanTier,
  tabs: 'pro' as PlanTier,
  inventory: 'pro' as PlanTier,
} as const;

export type PlanFeature = keyof typeof MIN_TIER;

/** Features lost when moving from `from` to a lower `to` (for downgrade copy). */
export const DOWNGRADE_FEATURE_KEYS: PlanFeature[] = [
  'postpaid',
  'tabs',
  'inventory',
  'timedMenus',
  'tableQr',
  'analytics',
];

export function resolvePlanTier(rawTier?: string | null): PlanTier {
  return (rawTier && rawTier in TIER_RANK ? rawTier : 'starter') as PlanTier;
}

/** True when `tier` is at or above `minimumTier` in the plan ranking. */
export function meetsMinimumTier(tier: PlanTier, minimumTier: PlanTier): boolean {
  return TIER_RANK[tier] >= TIER_RANK[minimumTier];
}

export function canUsePlanFeature(tier: PlanTier, feature: PlanFeature): boolean {
  return meetsMinimumTier(tier, MIN_TIER[feature]);
}

export function getVenueLimit(tier: PlanTier): number | null {
  return TIER_VENUE_LIMITS[tier];
}

export function tierRank(tier: PlanTier): number {
  return TIER_RANK[tier] ?? 0;
}

export function isTierDowngrade(from: PlanTier, to: PlanTier): boolean {
  return tierRank(to) < tierRank(from);
}

/** Features the org keeps today but would lose after downgrade to `target`. */
export function featuresLostOnDowngrade(current: PlanTier, target: PlanTier): PlanFeature[] {
  if (!isTierDowngrade(current, target)) return [];
  return DOWNGRADE_FEATURE_KEYS.filter(
    (feature) => canUsePlanFeature(current, feature) && !canUsePlanFeature(target, feature)
  );
}

export function venueExcessForTier(activeCount: number, target: PlanTier): number {
  const limit = getVenueLimit(target);
  if (limit === null) return 0;
  return Math.max(0, activeCount - limit);
}
